import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { isEmpty } from "@/utils/query-string";
import { Dispatch, RefObject, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";

import {
  createTechnician,
  CreateTechnicianProps,
  deleteTechnicians,
  getTechnician,
  getTechniciansList,
  GetTechniciansListProps,
  updateTechnician,
  UpdateTechnicianProps,
} from "@/services/technicians.service";

import {
  ActiveDirectoryTechnicianUser,
  getTechniciansActiveDirectoryUsers,
} from "@/services/technicians-ad.service";

import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { TLFRProps, getTechniciansFilter } from "./filter-obj";
import { TechniciansListResponse } from "./technicians.schema";

interface FetchTechniciansHandlerProps {
  setLoadingTechnicians: Dispatch<SetStateAction<boolean>>;
  setTechnicians: Dispatch<SetStateAction<TechniciansListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetTechniciansListProps>;
}

export const fetchTechniciansHandler = async ({
  setLoadingTechnicians,
  setTechnicians,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchTechniciansHandlerProps) => {
  try {
    if (showLoad) setLoadingTechnicians(true);

    const request: GetTechniciansListProps = {
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": (filters as any)?.["P.Records"] ?? 10,
    };

    if (filters?.nombreApellido) request.nombreApellido = filters.nombreApellido;

    const rawActivo = (filters as any)?.activo;
    const activoSelected = Array.isArray(rawActivo) ? rawActivo?.[0] : rawActivo;
    if (typeof activoSelected === "boolean") request.activo = activoSelected;
    else if (activoSelected === "true") request.activo = true;
    else if (activoSelected === "false") request.activo = false;

    const response = await getTechniciansList(request);
    setTechnicians(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TECHNICIANS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de técnicos.",
      });
    }
  } finally {
    if (showLoad) setLoadingTechnicians(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetTechniciansListProps> | undefined>
  >;
  setPage: Dispatch<SetStateAction<number>>;
  fetchTechnicians: (
    filters?: Partial<GetTechniciansListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchTechnicians,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetTechniciansListProps> = getTechniciansFilter(
    event as TLFRProps,
  );

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  setPage(1);
  await fetchTechnicians({ ...filterObj, "P.Pagina": 1 }, true);

  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event?: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  submitFilters,
  getValues,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("nombreApellido", "");
  setValue("activo", undefined);

  await submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface DeleteTechniciansHandlerProps {
  selection: string[];
  setLoadingDeleteTechnicians: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: Dispatch<SetStateAction<boolean>>;
  fetchTechnicians: (
    filters?: Partial<GetTechniciansListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  setSelection: Dispatch<SetStateAction<string[]>>;
}

export const handleDeleteTechniciansHandler = async ({
  fetchTechnicians,
  selection,
  setLoadingDeleteTechnicians,
  setOpenConfirm,
  setSelection,
}: DeleteTechniciansHandlerProps) => {
  try {
    setLoadingDeleteTechnicians(true);

    const idsToDelete = selection.map((id) => Number.parseInt(id));

    await deleteTechnicians({ Ids: idsToDelete });

    toaster.create({
      title: "Técnicos eliminados con éxito.",
      type: "success",
    });

    setSelection([]);
    setOpenConfirm(false);
    fetchTechnicians({}, false);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron eliminar los técnicos.",
    );

    toaster.create({
      type: "error",
      title: errorMessage,
    });
  } finally {
    setLoadingDeleteTechnicians(false);
  }
};

interface FetchTechnicianHandlerProps {
  id: number;
  setLoadingTechnician: Dispatch<SetStateAction<boolean>>;
  setValue: UseFormSetValue<any>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchTechnicianHandler = async ({
  id,
  setLoadingTechnician,
  setValue,
  navigate,
  fetchPermissions,
}: FetchTechnicianHandlerProps) => {
  try {
    setLoadingTechnician(true);

    const technician = await getTechnician({ id });
    if (!technician) return;

    const usuarioValue = technician?.usuario;
    if (usuarioValue) {
      setValue("usuario", {
        label: `${technician.nombreApellido} - ${technician.email}`,
        value: technician?.usuario,
      });
    }

    setValue("nombreApellido", technician.nombreApellido ?? "");
    setValue("email", technician.email ?? "");
    setValue("activo", [Boolean(technician.activo)]);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TECHNICIAN_DETAIL,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el técnico.",
      });
    }
  } finally {
    setLoadingTechnician(false);
  }
};

export interface TechnicianUpdateFormValues {
  activo?: unknown;
}

interface SubmitUpdateTechnicianHandlerProps {
  id: number;
  values: TechnicianUpdateFormValues;
  setLoadingSave: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitUpdateTechnicianHandler = async ({
  id,
  values,
  setLoadingSave,
  navigate,
  fetchPermissions,
}: SubmitUpdateTechnicianHandlerProps) => {
  try {
    setLoadingSave(true);

    const rawActivo = (values as any)?.activo;
    const activoSelected = Array.isArray(rawActivo) ? rawActivo?.[0] : rawActivo;
    const activo = Boolean(activoSelected);

    const objToSend: UpdateTechnicianProps = {
      id,
      activo,
    };

    await updateTechnician(objToSend);

    toaster.create({
      type: "success",
      title: "Técnico actualizado con éxito.",
    });

    navigate(AppRoutes.TECHNICIANS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TECHNICIAN_DETAIL,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo actualizar el técnico.",
      });
    }
  } finally {
    setLoadingSave(false);
  }
};

export interface TechnicianCreationFormValues {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
}

interface SubmitCreateTechnicianHandlerProps {
  values: TechnicianCreationFormValues;
  setLoadingCreate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitCreateTechnicianHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
}: SubmitCreateTechnicianHandlerProps) => {
  try {
    setLoadingCreate(true);

    const usuario = String(values?.usuario?.value ?? "");
    const nombreApellido = String(values?.nombreApellido ?? "");
    const email = String(values?.email ?? "");

    if (isEmpty(usuario) || isEmpty(nombreApellido)) return;

    const objToSend: CreateTechnicianProps = {
      usuario,
      nombreApellido,
      email,
    };

    await createTechnician(objToSend);

    toaster.create({
      type: "success",
      title: "Técnico creado con éxito.",
    });

    navigate(AppRoutes.TECHNICIANS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TECHNICIAN_CREATION,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo crear el técnico.",
      });
    }
  } finally {
    setLoadingCreate(false);
  }
};

export interface GetADOptionsHandlerProps {
  inputValue: string;
}

export const getADOptionsHandler = async ({
  inputValue,
}: GetADOptionsHandlerProps): Promise<Option[]> => {
  const filtered = await getTechniciansActiveDirectoryUsers({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: (
        `${p?.displayName ?? ""}`.trim() ||
        `${p?.givenName ?? ""} ${p?.surname ?? ""}`.trim() ||
        String(p.id)
      )
        .concat(p?.mail ? ` - ${p.mail}` : "")
        .trim(),
      value: p.id,
    })) ?? []
  );
};

export interface LoadADOptionsHandlerProps {
  timeoutRef: RefObject<ReturnType<typeof setTimeout> | null>;
  inputValue: string;
  setLoadingADUsers: Dispatch<SetStateAction<boolean>>;
  getADOptions: (inputValue: string) => Promise<Option[]>;
}

export const loadADOptionsHandler = ({
  timeoutRef,
  inputValue,
  setLoadingADUsers,
  getADOptions,
}: LoadADOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      try {
        if (inputValue.trim().length < 3) {
          setLoadingADUsers(false);
          resolve([]);
          return;
        }

        setLoadingADUsers(true);
        const options = await getADOptions(inputValue);
        resolve(options);
      } catch {
        resolve([]);
      } finally {
        setLoadingADUsers(false);
      }
    }, 1000);
  });
};

interface FetchDefaultADUsersHandlerProps {
  setLoadingADUsers: Dispatch<SetStateAction<boolean>>;
  setDefaultADUsers: Dispatch<SetStateAction<ActiveDirectoryTechnicianUser[]>>;
}

export const fetchDefaultADUsersHandler = async ({
  setLoadingADUsers,
  setDefaultADUsers,
}: FetchDefaultADUsersHandlerProps) => {
  try {
    setLoadingADUsers(true);
    const users = await getTechniciansActiveDirectoryUsers({});
    setDefaultADUsers(users ?? []);
  } catch {
    setDefaultADUsers([]);
  } finally {
    setLoadingADUsers(false);
  }
};

interface HandleUserSelectionHandlerProps {
  opt: Option | null;
  adUserById: Map<string, ActiveDirectoryTechnicianUser>;
  setValue: UseFormSetValue<any>;
}

export const handleUserSelectionHandler = async ({
  opt,
  adUserById,
  setValue,
}: HandleUserSelectionHandlerProps) => {
  if (!opt?.value) return;

  const id = String(opt.value);
  let user = adUserById.get(id);

  if (!user) {
    const fetched = await getTechniciansActiveDirectoryUsers({ busqueda: id });
    user = fetched?.find((u) => String(u.id) === id);
  }

  const fullName =
    `${user?.givenName ?? ""} ${user?.surname ?? ""}`.trim() ||
    (user?.displayName ?? "");

  setValue("nombreApellido", fullName);
  setValue("email", user?.mail ?? "");
};
